import React, { useCallback, useState } from 'react';

import { useMutatePlatformOwnerProjects } from '@hooks/portal/projects/platformOwnerProjects/useMutatePlatformOwnerProjects';
import { useQueryPlatformOwnerProjects } from '@hooks/portal/projects/platformOwnerProjects/useQueryPlatformOwnerProjects';
import {
	useQueryPlatformOwnerProjectAdmins,
} from '@hooks/portal/projects/platformOwnerProjects/useQueryPlatformOwnerProjectsAdmins';
import {
	useQueryPlatformOwnerProjectMembers,
} from '@hooks/portal/projects/platformOwnerProjects/useQueryPlatformOwnerProjectsMembers';
import { useQueryPlatformOwnerProjectStatuses } from '@hooks/portal/projects/platformOwnerProjects/useQueryPlatformOwnerProjectsStatuses';
import {
	useQueryPlatformOwnerProjectWorkflows,
} from '@hooks/portal/projects/platformOwnerProjects/useQueryPlatformOwnerProjectsWorkflows';

import { organisationProjects } from '@store/selectors';
import { useAppSelector } from '@store/store';

import ProjectsTable from '../ProjectsTable';

interface IPlatformOwnerProjectsContainer {
	organisationId: string;
}

const PlatformOwnerProjectsContainer = ({
	organisationId,
}: IPlatformOwnerProjectsContainer) => {
	const {
		searchValue,
		status,
	} = useAppSelector(organisationProjects);

	const [isShowDeleteProjectsConfirm, setIsShowDeleteProjectsConfirm] = useState(false);
	const [singleProjectIdForRemove, setSingleProjectIdForRemove] = useState<string | null>(null);

	const openDeleteProjectsConfirm = useCallback(() => {
		setIsShowDeleteProjectsConfirm(prevState => !prevState);
	}, []);

	const closeDeleteProjectsConfirm = useCallback(() => {
		setIsShowDeleteProjectsConfirm(false);
		setSingleProjectIdForRemove(null);
	}, []);

	const {
		projectsList,
		isLoadingProjects,
		countOfRecords,
	} = useQueryPlatformOwnerProjects(organisationId);

	const { projectsStatuses, isLoadingProjectsStatuses } = useQueryPlatformOwnerProjectStatuses();
	const { projectsWorkflows, isLoadingProjectsWorkflows } = useQueryPlatformOwnerProjectWorkflows();
	const { projectAdmins, isLoadingProjectsAdmins } = useQueryPlatformOwnerProjectAdmins(organisationId);
	const { projectMembers, isLoadingProjectsMembers } = useQueryPlatformOwnerProjectMembers(organisationId);

	const {
		createProject,
		updateProject,
		handleDeleteProject,
		handleDeleteProjects,
		isLoadingDelete,
		handleCheckProjectCode,
	} = useMutatePlatformOwnerProjects({
		organizationId: organisationId,
		searchValue,
		status,
		closeDeleteProjectsConfirm,
	});

	const isLoading = isLoadingProjects
		|| isLoadingProjectsStatuses
		|| isLoadingProjectsWorkflows
		|| isLoadingProjectsAdmins
		|| isLoadingProjectsMembers;

	return (
		<ProjectsTable
			closeDeleteProjectsConfirm={closeDeleteProjectsConfirm}
			countOfRecords={countOfRecords}
			createProject={createProject}
			handleCheckProjectCode={handleCheckProjectCode}
			handleDeleteProject={handleDeleteProject}
			handleDeleteProjects={handleDeleteProjects}
			isLoading={isLoading}
			isLoadingDelete={isLoadingDelete}
			isShowDeleteProjectsConfirm={isShowDeleteProjectsConfirm}
			openDeleteProjectsConfirm={openDeleteProjectsConfirm}
			organisationId={organisationId}
			projectAdmins={projectAdmins}
			projectMembers={projectMembers}
			projectsList={projectsList}
			projectsStatuses={projectsStatuses}
			projectsWorkflows={projectsWorkflows}
			setSingleProjectIdForRemove={setSingleProjectIdForRemove}
			singleProjectIdForRemove={singleProjectIdForRemove}
			updateProject={updateProject}
		/>
	);
};

export default PlatformOwnerProjectsContainer;
